/**
 * The scan that happens on the device, with no server.
 *
 * The app already owns half a scan without a network: it decodes the capture, finds
 * the card, classifies the frame and cuts and hashes the art window, all in the shared
 * core (ADR 0009). What it could not do was turn that fingerprint into anything,
 * because the illustrations live in the index and the index lived on the server.
 * `apps/web/src/ota/art-index.ts` installs it; this module is the search.
 *
 * What it answers is narrower than the server's answer, and deliberately so: an
 * illustration id, which is ADR 0004's "entry point to a candidate set, never an
 * answer". Turning it into a *printing* needs the catalog, which is on the server, so
 * an offline match cannot file a card by itself - it tells a person which artwork they
 * are holding, and the set code and number printed on the card finish the job.
 *
 * The search itself is the core's: `searchArtIndex` is the same exhaustive scan the
 * API's route runs against its own copy, so a device that matched here and a server
 * that matched there agree about what "near" means.
 */

import { hexToHash, searchArtIndex, searchArtIndexAny, type ArtIndex } from '@unsorted/shared';

/** An artwork a capture is near, in the index's own terms. */
export interface LocalArtMatch {
  illustrationId: string;
  /** Bits the capture's fingerprint differs from the indexed artwork by. */
  distance: number;
  similarity: number;
  /** Bits separating it from the nearest *other* artwork. */
  marginBits: number;
}

/**
 * The artwork a fingerprint is near in this index, or null.
 *
 * Null for two different things, both of which end the same way - the scan needs a
 * person, and the surface says why:
 *
 *  - **nothing is close enough.** `searchArtIndex` applies the threshold the pattern
 *    was measured with (ADR 0006), so a capture of some other card, a glare, a
 *    finger over the picture is refused rather than rounded to the nearest artwork.
 *  - **the fingerprint is not a fingerprint.** A 32-hex digest that arrived mangled
 *    from a bridge is not an artwork, and a scan that throws is a scan a person
 *    cannot retry.
 */
export function matchArtworkLocally(index: ArtIndex, artPhash: string | readonly string[]): LocalArtMatch | null {
  const hashes = typeof artPhash === 'string' ? [artPhash] : artPhash;
  let probes: Uint8Array[];
  try {
    probes = hashes.map((hash) => hexToHash(hash, index.bits));
  } catch {
    return null;
  }
  if (probes.length === 0) return null;

  // The capture's framing is uncertain, so the same rule as the server's: an artwork is
  // as near as its best framing makes it.
  const result =
    probes.length === 1
      ? searchArtIndex(index, probes[0] as Uint8Array)
      : searchArtIndexAny(index, probes);
  if (!result.matched || result.best === null) return null;

  return {
    illustrationId: result.best.illustrationId,
    distance: result.best.distance,
    similarity: result.best.similarity,
    marginBits: result.marginBits,
  };
}

/**
 * The match, as a note under a capture - for the explanation a review item opens,
 * where the illustration id and the distance are what a support thread needs.
 */
export function describeLocalMatch(match: LocalArtMatch): string {
  return (
    `the artwork matches illustration ${match.illustrationId} ${match.distance} bits away, ` +
    'matched on this device because the catalog could not be reached'
  );
}
